import React, { Component, ReactNode } from 'react';
import WarningIcon from '@mui/icons-material/Warning';
import { Box, Button, Typography, Dialog } from '@mui/material';
import * as Sentry from '@sentry/react';

interface Props {
  children: ReactNode;
  onClose?: () => void;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export class ModalErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
    };
  }

  static getDerivedStateFromError(error: Error): State {
    return {
      hasError: true,
      error,
    };
  }

  componentDidCatch(error: Error) {
    Sentry.captureException(error);
  }

  handleClose = () => {
    if (this.props.onClose) {
      this.props.onClose();
    }
  };

  handleRetry = () => {
    this.setState({
      hasError: false,
      error: null,
    });
  };

  render() {
    if (this.state.hasError) {
      return (
        <Dialog open={true} onClose={this.handleClose} maxWidth='md' fullWidth>
          <Box
            sx={{
              p: 3,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 2,
              minHeight: 200,
              justifyContent: 'center',
            }}
          >
            <WarningIcon sx={{ fontSize: 48, color: 'warning.main' }} />

            <Typography
              variant='h6'
              sx={{
                textAlign: 'center',
              }}
            >
              Modal Error
            </Typography>

            <Typography
              variant='body2'
              sx={{
                color: 'text.secondary',
                textAlign: 'center',
              }}
            >
              Something went wrong in this dialog. Please try again or close and
              reopen.
            </Typography>

            <Box
              sx={{
                display: 'flex',
                gap: 1,
                mt: 1,
              }}
            >
              <Button
                variant='outlined'
                size='small'
                onClick={this.handleRetry}
              >
                Try Again
              </Button>
              {this.props.onClose && (
                <Button
                  variant='contained'
                  size='small'
                  onClick={this.handleClose}
                >
                  Close
                </Button>
              )}
            </Box>
          </Box>
        </Dialog>
      );
    }

    return this.props.children;
  }
}
